/**
 * 认证契约 —— **唯一契约源**（架构 §6.1 / §6.2 · §11）
 *
 * 依据：架构 **§6.2**（`Authorization: Bearer <JWT>` 14 天）· **§11 认证**（JWT + bcryptjs）·
 * `DR-018` §3.4（🔴 **注册不变量**：新注册用户默认有 1 本账本 ⇒「1 用户 ≥ 1 账本」）。
 *
 * ## 🔴 本文件刻意不包含 `ledgerId` 的入参
 * 架构 §6.2 铁律：`ledgerId` **只来自 `X-Ledger-Id` 经守卫推导**，
 * **禁止取自 URL query / body**，🔴 **也不得从 JWT 推导**（多账本切换要求**请求级**上下文）。
 * ⇒ 请求体里**根本没有** `ledgerId` 这个字段 —— 不是"忘了写"，是**刻意的**：
 * 契约层就不给出"从 body 传租户"的可能。
 */

import { z } from 'zod';
import { UserRole } from '../enums/identity.js';
// 🔴 账本名 schema 归 **L 域契约**（`collaboration.ts`）—— 账本实体归 L 域（`DR-018` §6.4-6）；
//    本文件只是注册流程顺手带上它，不在这里再定义一份（同名两份 = 双实现）。
import { LedgerNameSchema } from './collaboration.js';

/**
 * UTF-8 字节长度 —— 用于密码上限校验。
 *
 * 🔴 **为什么需要**：bcrypt **按字节截断至 72**，超出部分**被静默丢弃**。
 * 中文一字 3 字节 ⇒「72 字的中文密码」实际只有前 24 字有效，用户以为更安全其实不然。
 * 这里**显式拒绝**而不是静默截断（拒绝的方向是安全的）。
 *
 * ⚠️ 手算而不用 `Buffer` / `TextEncoder`：本文件被 **Web 端与小程序端**共同消费，
 * 不能依赖 Node 专有全局对象。
 */
export function utf8ByteLength(text: string): number {
  let bytes = 0;
  for (const ch of text) {
    const cp = ch.codePointAt(0) ?? 0;
    bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
  }
  return bytes;
}

/** bcrypt 的硬上限（字节）—— **不可放宽**，放宽等于接受静默截断 */
export const BCRYPT_MAX_BYTES = 72;

/**
 * 邮箱。
 *
 * 🔴 **顺序是关键**：`z.email()` 是**即时校验**，而 `.trim()` / `.toLowerCase()` 是
 * **后置变换** —— 写成 `z.email().trim().toLowerCase()` 时，格式检查在规整**之前**跑，
 * ⇒ `'  A@B.com '` 这种"用户从别处复制来的邮箱"会被**直接拒掉**。
 * （本文件第一版就是这么写的，被 `auth.dto.spec.ts` 当场抓出。）
 * 正解 = **先规整、再校验**：`.pipe()` 把变换后的值交给邮箱校验。
 *
 * 规整本身是必须的：不做的话 `A@b.com` 与 `a@b.com` 会变成**两个账号**，
 * 而用户登录时输入哪个全凭手感。
 */
export const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email({ message: '邮箱格式不正确' }).max(254, { message: '邮箱过长' }));

/** 手机号（中国大陆 11 位）—— 同样是「先 trim 再校验」 */
export const PhoneSchema = z
  .string()
  .trim()
  .regex(/^1[3-9]\d{9}$/, '手机号格式不正确');

/**
 * 密码。
 *
 * 🔴 下限 8 位（规格未定，取业界通用下限）；上限 = **字节** 72（bcrypt 截断点）。
 * 不要求字符类别（大小写/数字/符号）—— 那类规则逼出 `Password1!` 这种弱密码，
 * 长度才是有效维度。
 */
export const PasswordSchema = z
  .string()
  .min(8, '密码至少 8 位')
  .refine((s) => utf8ByteLength(s) <= BCRYPT_MAX_BYTES, {
    message: `密码过长：bcrypt 按字节截断至 ${BCRYPT_MAX_BYTES}（中文一字 3 字节），超出部分会被静默丢弃`,
  });

/** 展示名 */
export const DisplayNameSchema = z.string().trim().min(1, '姓名不得为空').max(32, '姓名最长 32 字');

/**
 * 注册。
 *
 * 🔴 `ledgerName` 可省 —— 缺省 = `{姓名}的家庭`（`DR-018` §3.4「注册即建『{姓名}的家庭』」）。
 * 注册**必然**建账本 + owner 成员 + 预置分类，这三件事在**同一事务**内完成（不能只建用户）。
 */
export const RegisterRequestSchema = z.object({
  email: EmailSchema,
  password: PasswordSchema,
  name: DisplayNameSchema,
  ledgerName: LedgerNameSchema.optional(),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

/** 登录 */
export const LoginRequestSchema = z.object({
  email: EmailSchema,
  password: z.string().min(1, '密码不得为空'),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/** 对外可见的用户视图 —— 🔴 **绝不含 `passwordHash`**（规格 §11：导出排除表含 `users`） */
export interface AuthUserView {
  id: string;
  email: string | null;
  phone: string | null;
  name: string;
  /** 🔴 系统级角色，与账本角色（`owner`/`member`）**解耦** */
  role: UserRole;
}

/**
 * 登录 / 注册成功的响应。
 *
 * `ledgerId` 只在**注册**时返回（新建的默认账本，省一次往返）；
 * 登录响应里**没有**它 —— 登录后客户端要调 `GET /api/ledgers` 拿列表，
 * 因为「当前账本」是**请求级上下文**，不该由登录响应替客户端决定
 * （用户上次用的是哪本，存在客户端本地）。
 */
export interface AuthTokenResponse {
  token: string;
  /** UTC 毫秒（= 签发时刻 + 14 天） */
  expiresAt: number;
  user: AuthUserView;
}

export interface RegisterResponse extends AuthTokenResponse {
  /** 注册时**必然**新建的默认账本 id */
  ledgerId: string;
}
